import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

interface User {
  userId: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isEmbedMode: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Trusted origins that can send user auth via postMessage
const TRUSTED_ORIGINS = [
  'https://cloudops.umetech.net',
  'https://calm-coast-0045b821e.3.azurestaticapps.net', // UMT-HUD SWA
  'http://localhost:5173', // Local dev
  'http://localhost:3000',
];

// Check if running in an iframe (HUD embed mode)
function isEmbedMode(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // If we can't access window.top, we're in a cross-origin iframe
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const embedMode = isEmbedMode();

  // Listen for user auth from HUD via postMessage
  const handleMessage = useCallback((event: MessageEvent) => {
    // Verify origin is trusted
    if (!TRUSTED_ORIGINS.includes(event.origin)) {
      return;
    }

    // Check for HUD_USER_AUTH message
    if (event.data?.type === 'HUD_USER_AUTH' && event.data?.email) {
      console.log('Received HUD_USER_AUTH:', event.data.email);
      setUser({
        userId: 'hud-user',
        email: event.data.email,
        name: event.data.email.split('@')[0]
      });
    }
  }, []);

  // Set up message listener for embed mode and request auth from parent
  useEffect(() => {
    if (embedMode) {
      window.addEventListener('message', handleMessage);

      // Proactively request auth from parent HUD
      try {
        window.parent.postMessage({ type: 'REQUEST_AUTH' }, '*');
      } catch {
        // Ignore if parent is inaccessible
      }

      return () => window.removeEventListener('message', handleMessage);
    }
  }, [embedMode, handleMessage]);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      // Try to get the real authenticated user from SWA auth
      const response = await fetch('/.auth/me');
      const data = await response.json();

      if (data.clientPrincipal) {
        // Got real user from SWA auth
        setUser({
          userId: data.clientPrincipal.userId,
          email: data.clientPrincipal.userDetails,
          name: data.clientPrincipal.userDetails
        });
      } else if (embedMode) {
        // In embed mode (HUD iframe), auth cookie doesn't transfer across domains
        // Trust that HUD has authenticated the user - set placeholder
        // TODO: Use postMessage to get actual user email from HUD for proper RBAC
        setUser({
          userId: 'embed-user',
          email: 'embed@hud.local',
          name: 'HUD User'
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // In embed mode, still allow access even if auth check fails
      if (embedMode) {
        setUser({
          userId: 'embed-user',
          email: 'embed@hud.local',
          name: 'HUD User'
        });
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }

  function login() {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
  }

  function logout() {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  }

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      isEmbedMode: embedMode,
      login,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
